import React from 'react';

import { faPlus } from '@fortawesome/free-solid-svg-icons/faPlus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Spinner } from '@admin/components/spinner';
import { facilityServiceTypeURL, facilityURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { useFacilitiesListQuery } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { CardToolbarLead } from '@admin/styled/card_toolbar_lead';
import { CodeText } from '@admin/styled/code_text';
import { DescriptionCell } from '@admin/styled/description_cell';
import { EmptyState } from '@admin/styled/empty_state';
import { MutedText } from '@admin/styled/muted_text';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageTitle } from '@admin/styled/page_title';
import { Pill } from '@admin/styled/pill';
import { PillGroup } from '@admin/styled/pill_group';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionMeta } from '@admin/styled/section_meta';
import { SectionTitle } from '@admin/styled/section_title';
import { SlugChip } from '@admin/styled/slug_chip';
import { Table } from '@admin/styled/table';
import { TableWrap } from '@admin/styled/table_wrap';

import { formatFacilityAddress } from './format_address';

export const FacilitiesList: React.FC = () => {
  useTitle('Facilities');
  const canCreate = usePermissionKey('facility:create');
  const [showInactive, setShowInactive] = React.useState(false);
  const { data, loading } = useFacilitiesListQuery({
    client,
    variables: { includeInactive: true },
  });

  if (loading && !data) {
    return <Spinner />;
  }

  const allFacilities = data?.facilities ?? [];
  const activeFacilities = allFacilities.filter((facility) => facility.active);
  const inactiveFacilities = allFacilities.filter((facility) => !facility.active);
  const facilities = showInactive ? allFacilities : activeFacilities;
  const inactiveCount = inactiveFacilities.length;
  const serviceTypes = data?.facilityServiceTypes ?? [];

  return (
    <Page>
      <PageHeader>
        <PageTitle>Facilities</PageTitle>
      </PageHeader>

      <Section>
        <Card>
          <CardToolbar>
            <CardToolbarLead>
              <SectionTitle>Facility Sites</SectionTitle>
              <SectionMeta>
                {showInactive
                  ? `${allFacilities.length} ${allFacilities.length === 1 ? 'facility' : 'facilities'}`
                  : `${activeFacilities.length} active`}
                {!showInactive && inactiveCount > 0 && ` · ${inactiveCount} hidden`}
              </SectionMeta>
            </CardToolbarLead>
            <SectionActions>
              {inactiveCount > 0 && (
                <Button variant="secondary" size="sm" type="button" onClick={() => setShowInactive((value) => !value)}>
                  {showInactive ? 'Hide inactive' : `Show ${inactiveCount} inactive`}
                </Button>
              )}
              {canCreate && (
                <ButtonLink variant="primary" size="sm" to="/facilities/new">
                  <FontAwesomeIcon icon={faPlus} /> New facility
                </ButtonLink>
              )}
            </SectionActions>
          </CardToolbar>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Address</th>
                  <th>Regions</th>
                  <th>Service types</th>
                  <th className="col-status">Status</th>
                </tr>
              </thead>
              <tbody>
                {facilities.length === 0 ? (
                  <tr>
                    <td colSpan={6}>
                      <EmptyState>
                        {activeFacilities.length === 0 && inactiveCount > 0 && !showInactive
                          ? 'No active facilities. Show inactive to view archived sites.'
                          : 'No facilities yet.'}
                      </EmptyState>
                    </td>
                  </tr>
                ) : (
                  facilities.map((facility) => (
                    <tr key={facility.id}>
                      <td>
                        <ButtonLink to={facilityURL({ id: facility.id })}>{facility.name}</ButtonLink>
                      </td>
                      <td>
                        <CodeText>{facility.code}</CodeText>
                      </td>
                      <td>{formatFacilityAddress(facility.address) ?? <MutedText>Not set</MutedText>}</td>
                      <td>
                        {facility.regions.length > 0 ? (
                          <PillGroup>
                            {facility.regions.map((region) => (
                              <Pill key={region.id} tone="muted">
                                {region.name}
                              </Pill>
                            ))}
                          </PillGroup>
                        ) : (
                          <MutedText>None</MutedText>
                        )}
                      </td>
                      <td>
                        {facility.serviceTypes.length > 0 ? (
                          <PillGroup>
                            {facility.serviceTypes.map((st) => (
                              <Pill key={st.id} tone="accent">
                                {st.name}
                              </Pill>
                            ))}
                          </PillGroup>
                        ) : (
                          <MutedText>None</MutedText>
                        )}
                      </td>
                      <td className="col-status">
                        <Pill tone={facility.active ? 'success' : 'muted'}>
                          {facility.active ? 'Active' : 'Inactive'}
                        </Pill>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      </Section>

      <Section>
        <Card>
          <CardToolbar>
            <CardToolbarLead>
              <SectionTitle>Service Types</SectionTitle>
              <SectionMeta>
                {serviceTypes.length} {serviceTypes.length === 1 ? 'service type' : 'service types'}
              </SectionMeta>
            </CardToolbarLead>
          </CardToolbar>
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {serviceTypes.length === 0 ? (
                  <tr>
                    <td colSpan={3}>
                      <EmptyState>No service types configured.</EmptyState>
                    </td>
                  </tr>
                ) : (
                  serviceTypes.map((serviceType) => (
                    <tr key={serviceType.id}>
                      <td>
                        <ButtonLink to={facilityServiceTypeURL({ id: serviceType.id })}>{serviceType.name}</ButtonLink>
                      </td>
                      <td>
                        <SlugChip>{serviceType.slug}</SlugChip>
                      </td>
                      <td>
                        <DescriptionCell>{serviceType.description}</DescriptionCell>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableWrap>
        </Card>
      </Section>
    </Page>
  );
};
